import { Button, Card, Flex, Typography } from 'antd'
import {
  AlertTriangle,
  Key,
  MapPin,
  ShieldAlert,
  type LucideIcon
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { SoonTag } from 'src/components'
import { paths } from 'src/router'

import { useStyles } from './styles'

const { Text } = Typography

type Signal = {
  id: string
  icon: LucideIcon
  color: string
  bg: string
  title: string
  meta: string
  cta: string
}

// Sample security signals — illustrative until threat-intel/security endpoints ship.
const SIGNALS: Signal[] = [
  {
    id: 'brute-force',
    icon: AlertTriangle,
    color: 'var(--ant-color-error)',
    bg: 'rgba(220, 38, 38, 0.1)',
    title: 'Brute-force attempt blocked',
    meta: 'IP 203.0.113.7 · 5 failed logins · 08:46',
    cta: 'Review'
  },
  {
    id: 'mfa-missing',
    icon: ShieldAlert,
    color: 'var(--ant-color-warning)',
    bg: 'rgba(217, 119, 6, 0.1)',
    title: '2 admins without MFA',
    meta: 'Policy requires MFA for admin role',
    cta: 'Enforce'
  },
  {
    id: 'new-location',
    icon: MapPin,
    color: 'var(--ant-color-info)',
    bg: 'rgba(37, 99, 235, 0.1)',
    title: 'Sign-in from new location',
    meta: 'Berlin, DE · 07:55',
    cta: 'Approve'
  },
  {
    id: 'stale-key',
    icon: Key,
    color: 'var(--ant-color-warning)',
    bg: 'rgba(217, 119, 6, 0.1)',
    title: '1 API key not rotated > 90 days',
    meta: 'legacy-migration · created 2026-02-12',
    cta: 'Rotate'
  }
]

type Props = {
  workspaceIdParam: string
}

export const OverviewSecuritySignals = ({ workspaceIdParam }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  return (
    <Card variant="outlined" className={styles.panelCard}>
      <Flex className={styles.panelHeader}>
        <Flex align="center" gap={6}>
          <Text className={styles.panelTitle}>
            {t('workspaceOverview.securitySignals.title')}
          </Text>
          <SoonTag />
        </Flex>
        <Link
          to={paths.workspaces.audit(workspaceIdParam)}
          style={{ fontSize: 12 }}
        >
          {t('workspaceOverview.securitySignals.viewAll')}
        </Link>
      </Flex>

      <Flex vertical>
        {SIGNALS.map((signal) => {
          const Icon = signal.icon

          return (
            <Flex key={signal.id} className={styles.signalRow}>
              <span
                className={styles.signalIcon}
                style={{ background: signal.bg, color: signal.color }}
                aria-hidden
              >
                <Icon size={14} />
              </span>
              <Flex
                vertical
                style={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}
                gap={2}
              >
                <Text
                  strong
                  style={{ fontSize: 12.5 }}
                  ellipsis={{ tooltip: signal.title }}
                >
                  {signal.title}
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  ellipsis={{ tooltip: signal.meta }}
                >
                  {signal.meta}
                </Text>
              </Flex>
              <Button
                size="small"
                type="link"
                style={{ paddingInline: 0, fontSize: 11 }}
                disabled
              >
                {signal.cta}
              </Button>
            </Flex>
          )
        })}
      </Flex>
    </Card>
  )
}
